import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  Link,
} from '@mui/material'
import { format, sub } from 'date-fns'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'

import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  CompareArrows as CompareIcon,
  PlaylistAddCheck as RequestAnnotationIcon,
} from '@mui/icons-material'
import { useNotification } from '@refinedev/core'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { dataProvider } from '../providers/dataProvider'
import {
  annotationColour,
  annotationLabel,
  type AnnotationStatus,
} from '../interfaces/matchRunInterfaces'
import {
  Scan,
  ScanFilter,
  ScanSummary as ScanSummaryType,
  ScanResponse,
  SummaryQuery,
  ScansQuery,
} from '../interfaces/scanInterfaces'
import ScanDashboardMatchDetails from './ScanDashboardMatchDetails'

const COLOURS = ['#FF8042', '#FFBB28', '#00C49F', '#0088FE', '#8884D8']

const getDateRangeForTimeframe = (
  timeframe: string
): { start?: string; end: string } => {
  const now = new Date()
  let startDate: Date | undefined

  switch (timeframe) {
    case '7d':
      startDate = sub(now, { days: 7 })
      break
    case '30d':
      startDate = sub(now, { days: 30 })
      break
    case '90d':
      startDate = sub(now, { days: 90 })
      break
    case 'all':
      // No start date for 'all'
      startDate = undefined
      break
    default:
      startDate = sub(now, { days: 30 })
  }

  return {
    start: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
    end: format(now, 'yyyy-MM-dd'),
  }
}

interface StrategyCoverage {
  key: string
  model: string
  prompt: string
  label: string
  annotations: number
  scans: number
  matches: number
  annotators: number
  last_annotated_at: string | null
}

const ScanDashboard = () => {
  const notify = useNotification()

  const [timeframe, setTimeframe] = useState<'7d' | '30d' | '90d' | 'all'>(
    '30d'
  )
  const [selectedOrg, setSelectedOrg] = useState<string>('')
  const [selectedNeed, setSelectedNeed] = useState<number | ''>('')
  const [selectedFunder, setSelectedFunder] = useState<string>('')
  const [selectedOpportunity, setSelectedOpportunity] = useState<string>('')
  const [filters, setFilters] = useState<ScanFilter>({})
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchTermApplied, setSearchTermApplied] = useState<string | null>(
    null
  )

  const [scans, setScans] = useState<Scan[]>([])
  // Annotation coverage per candidate strategy. Independent of the timeframe and
  // filters above: the question is how much evidence each strategy has in total,
  // not how much arrived this month.
  const navigate = useNavigate()

  const [coverage, setCoverage] = useState<StrategyCoverage[] | null>(null)
  // The strategy whose prompt is open, and the fetched template. Kept separate so
  // the dialog can show its title while the body is still loading.
  // Scans marked as wanting annotation, keyed `need:batch`. Held here rather than
  // refetched per row so toggling one does not reload the table.
  const [requested, setRequested] = useState<Set<string>>(new Set())
  // Annotation progress per scan, keyed `need:batch`. A scan MISSING from this map
  // has no runs of the active variant, which is a different state from "nothing
  // annotated" - see annotationColour below.
  const [annotationStatus, setAnnotationStatus] = useState<
    Map<string, AnnotationStatus>
  >(new Map())
  const [promptFor, setPromptFor] = useState<StrategyCoverage | null>(null)
  const [promptText, setPromptText] = useState<string | null>(null)

  const [scanSummary, setScanSummary] = useState<ScanSummaryType>({
    total_scans: 0,
    average_score: 0,
    high_ranking_matches: 0,
    total_organisations: 0,
    total_potential_matches: 0,
    high_ranking_percentage: 0,
    scan_count_by_period: [],
    score_distribution: [],
    top_organisations: [],
    top_funders: [],
    organisations: [],
    funding_needs: [],
    funders: [],
    opportunities: [],
  })

  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  // `number | undefined`, never NaN - see totalFrom in providers/dataProvider.ts.
  const [totalItems, setTotalItems] = useState<number | undefined>(undefined)

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedScanBatch, setSelectedScanBatch] = useState<number | null>(
    null
  )

  const [sortField, setSortField] = useState<'date' | 'total' | 'high'>('date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const [summaryLoading, setSummaryLoading] = useState(false)
  const scanRequest = useRef(0)
  const summaryCache = useRef<{key: string; at: number; value: Promise<{data: unknown}>} | null>(null)

  // Fetch scan data
  const fetchScanData = useCallback(async () => {
    const request = ++scanRequest.current
    setLoading(true)
    setError(null)
    try {
      // Get summary data first
      const summaryQuery: SummaryQuery = {
        timeframe: timeframe,
      }

      if (filters.organisation_uuid) {
        summaryQuery['org_uuid'] = filters.organisation_uuid
      }

      if (filters.funding_need_id) {
        summaryQuery['need_id'] = filters.funding_need_id.toString()
      }

      if (filters.funder_uuid) {
        summaryQuery['funder_uuid'] = filters.funder_uuid
      }

      if (filters.opportunity_uuid) {
        summaryQuery['opportunity_uuid'] = filters.opportunity_uuid
      }

      setSummaryLoading(true)
      void (async () => {
        try {
          if (!dataProvider || !dataProvider.custom) {
            notify?.open?.({
              type: 'error',
              message: 'Failed to load scan dashboard data',
              description: 'Please try again later',
            })
          } else {
            const key = JSON.stringify(summaryQuery)
          if (!summaryCache.current || summaryCache.current.key !== key || Date.now() - summaryCache.current.at > 30000) {
            summaryCache.current = { key, at: Date.now(), value: dataProvider.custom({
              url: '/scan-results/summary', method: 'get', query: summaryQuery,
            }) }
          }
          const summaryResponse = await summaryCache.current.value

            // The filter option lists come from /filter-options now and are not in
            // this response; carry them across so a timeframe change does not empty
            // the dropdowns.
            if (request !== scanRequest.current) return
            setScanSummary((previous) => ({
              ...(summaryResponse.data as ScanSummaryType),
              organisations: previous.organisations,
              funders: previous.funders,
              opportunities: previous.opportunities,
              funding_needs: previous.funding_needs,
            }))
          }
        } catch (summaryErr) {
        if (request !== scanRequest.current) return
        summaryCache.current = null
          notify?.open?.({
            type: 'error',
            message: 'Failed to load scan dashboard summary data',
            description: 'Please try again later',
          })
          // Summary failure does not block the scan list.
        } finally {
          if (request === scanRequest.current) setSummaryLoading(false)
        }
      })()

      // Get scans directly using dataProvider
      try {
        // Prepare query parameters for scans
        const dateRange = getDateRangeForTimeframe(timeframe)

        // Use pagination parameters for the API call
        const startIndex = page * rowsPerPage
        const endIndex = startIndex + rowsPerPage

        // Map UI sort field to API sort field
        const apiSortField = sortFieldMapping[sortField] || 'scan_date'

        const scansQuery = {
          _sort: apiSortField,
          _order: sortOrder,
          _start: startIndex.toString(),
          _end: endIndex.toString(),
          timeframe: timeframe,
        } as ScansQuery

        if (filters.organisation_uuid) {
          scansQuery['org_uuid'] = filters.organisation_uuid
        }

        if (filters.funding_need_id) {
          scansQuery['need_id'] = filters.funding_need_id.toString()
        }

        if (filters.funder_uuid) {
          scansQuery['funder_uuid'] = filters.funder_uuid
        }

        if (filters.opportunity_uuid) {
          scansQuery['opportunity_uuid'] = filters.opportunity_uuid
        }

        // Add search term if available
        if (searchTerm.trim() !== '') {
          scansQuery['q'] = searchTerm.trim()
          setSearchTermApplied(searchTerm.trim())
        }

        // Use dataProvider to fetch scans
        if (dataProvider && dataProvider.custom) {
          const scansResponse = await dataProvider.custom({
            url: '/scans/', // Call the scans endpoint
            method: 'get',
            query: scansQuery,
          })
          // Get the scan data directly from the API (already grouped by batch number)
          if (request !== scanRequest.current) return
          const scanData = scansResponse.data as Scan[]

          // Undefined when the total header was unreadable. Never NaN.
          const headerTotalCount = (scansResponse as ScanResponse).total
          // Store the total count in state for pagination
          setTotalItems(headerTotalCount)

          // Set the scans directly (no processing needed)
          setScans(scanData)
        }
      } catch (scansErr) {
        if (request !== scanRequest.current) return
        notify?.open?.({
          type: 'error',
          message: 'Failed to load scan dashboard data',
          description: 'Please try again later',
        })
        setScans([])
      }
    } catch (err) {
      if (request !== scanRequest.current) return
      notify?.open?.({
        type: 'error',
        message: 'Failed to load scan dashboard data',
        description: 'Please try again later',
      })
      setError('Failed to load scan dashboard data. Please try again later.')

      // Set empty results to prevent UI from breaking
      setScans([])
      setScanSummary({
        total_scans: 0,
        average_score: 0,
        high_ranking_matches: 0,
        total_organisations: 0,
        total_potential_matches: 0,
        high_ranking_percentage: 0,
        scan_count_by_period: [],
        score_distribution: [],
        top_organisations: [],
        top_funders: [],
        organisations: [],
        funding_needs: [],
        funders: [],
        opportunities: [],
      })
    } finally {
      if (request === scanRequest.current) setLoading(false)
    }
  }, [timeframe, filters, notify, page, rowsPerPage, sortField, sortOrder])

  // Update data when timeframe, filters, page, or rows per page change
  useEffect(() => {
    fetchScanData()
  }, [timeframe, filters, page, rowsPerPage, sortField, sortOrder])

  // The filter dropdowns, fetched ONCE. They are SELECT DISTINCT over the whole of
  // scan_results and do not change with the timeframe or any filter, so refetching
  // them on every dashboard refresh cost 1.76s of a 1.78s summary request.
  const toggleAnnotationRequest = useCallback(
    async (needId: number, batchNumber: number, isRequested: boolean) => {
      if (!dataProvider?.custom) return
      const key = `${needId}:${batchNumber}`
      // Optimistic: the control is a toggle and the round trip is slower than the
      // click. Reverted below if the write fails.
      setRequested((previous) => {
        const next = new Set(previous)
        if (isRequested) next.delete(key)
        else next.add(key)
        return next
      })
      try {
        await dataProvider.custom({
          url: '/match-runs/annotation-requests',
          method: isRequested ? 'delete' : 'post',
          ...(isRequested
            ? { query: { need_id: needId, batch_number: batchNumber } }
            : { payload: { need_id: needId, batch_number: batchNumber } }),
        })
      } catch {
        setRequested((previous) => {
          const next = new Set(previous)
          if (isRequested) next.add(key)
          else next.delete(key)
          return next
        })
        notify?.open?.({
          type: 'error',
          message: 'Could not change the annotation request',
        })
      }
    },
    [notify]
  )

  const openPrompt = useCallback(async (strategy: StrategyCoverage) => {
    setPromptFor(strategy)
    setPromptText(null)
    if (!dataProvider?.custom) return
    try {
      const response = await dataProvider.custom({
        url: '/match-runs/prompt',
        method: 'get',
        query: { prompt: strategy.prompt },
      })
      const body = response.data as unknown as { template: string }
      setPromptText(body.template)
    } catch {
      setPromptText('Could not load this prompt.')
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const loadCoverage = async () => {
      if (!dataProvider?.custom) return
      const response = await dataProvider.custom({
        url: '/match-runs/annotation-coverage',
        method: 'get',
      })
      if (cancelled) return
      setCoverage(response.data as unknown as StrategyCoverage[])
    }
    const loadStatus = async () => {
      if (!dataProvider?.custom) return
      const response = await dataProvider.custom({
        url: '/match-runs/annotation-status',
        method: 'get',
      })
      if (cancelled) return
      const rows = response.data as unknown as {
        need_id: number
        batch_number: number
        status: AnnotationStatus
      }[]
      setAnnotationStatus(
        new Map(rows.map((r) => [`${r.need_id}:${r.batch_number}`, r.status]))
      )
    }

    const loadRequests = async () => {
      if (!dataProvider?.custom) return
      const response = await dataProvider.custom({
        url: '/match-runs/annotation-requests',
        method: 'get',
      })
      if (cancelled) return
      const rows = response.data as unknown as {
        need_id: number
        batch_number: number
      }[]
      setRequested(new Set(rows.map((r) => `${r.need_id}:${r.batch_number}`)))
    }
    // A failed load leaves the table out rather than blocking the dashboard: this
    // is a side report, and the scan metrics below it are the page's job.
    loadCoverage().catch(() => setCoverage([]))
    // Same: without this the toggles all render as un-requested after a refresh,
    // which reads as "nothing is queued" rather than "this failed to load".
    loadRequests().catch(() =>
      notify?.open?.({
        type: 'error',
        message: 'Could not load the annotation requests',
      })
    )
    // Left empty on failure, which renders every scan as "no runs" rather than
    // inventing progress that was never confirmed.
    loadStatus().catch(() => setAnnotationStatus(new Map()))
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const loadOptions = async () => {
      if (!dataProvider?.custom) return
      const response = await dataProvider.custom({
        url: '/scan-results/filter-options',
        method: 'get',
      })
      if (cancelled) return
      const options = response.data as unknown as Pick<
        ScanSummaryType,
        'organisations' | 'funders' | 'opportunities' | 'funding_needs'
      >
      setScanSummary((previous) => ({ ...previous, ...options }))
    }
    loadOptions().catch(() =>
      notify?.open?.({
        type: 'error',
        message: 'Could not load the scan filter options',
      })
    )
    return () => {
      cancelled = true
    }
  }, [])

  const handleTimeframeChange = (
    event: React.MouseEvent<HTMLElement>,
    newTimeframe: '7d' | '30d' | '90d' | 'all'
  ) => {
    if (newTimeframe !== null) {
      setTimeframe(newTimeframe)
    }
  }

  const handleOrgChange = (orgUuid: string) => {
    setSelectedOrg(orgUuid)

    // Update filters
    if (orgUuid) {
      setFilters((prev) => ({ ...prev, organisation_uuid: orgUuid }))
    } else {
      const { organisation_uuid, ...restFilters } = filters
      setFilters(restFilters)
    }

    // Reset pagination
    setPage(0)
  }

  const handleNeedChange = (needId: number | '') => {
    setSelectedNeed(needId)

    // Update filters
    if (needId !== '') {
      setFilters((prev) => ({ ...prev, funding_need_id: needId as number }))
    } else {
      const { funding_need_id, ...restFilters } = filters
      setFilters(restFilters)
    }

    // Reset pagination
    setPage(0)
  }

  const handleFunderChange = (funderUuid: string) => {
    setSelectedFunder(funderUuid)

    // Reset opportunity selection if funder changes
    if (selectedOpportunity && funderUuid === '') {
      setSelectedOpportunity('')
    }

    // Update filters
    if (funderUuid) {
      setFilters((prev) => ({
        ...prev,
        funder_uuid: funderUuid,
      }))
    } else {
      const { funder_uuid, opportunity_uuid, ...restFilters } = filters
      setFilters(restFilters)
    }

    // Reset pagination
    setPage(0)
  }

  const handleOpportunityChange = (opportunityUuid: string) => {
    setSelectedOpportunity(opportunityUuid)

    // Update filters
    if (opportunityUuid) {
      setFilters((prev) => ({
        ...prev,
        opportunity_uuid: opportunityUuid,
      }))
    } else {
      const { opportunity_uuid, ...restFilters } = filters
      setFilters(restFilters)
    }

    // Reset pagination
    setPage(0)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Handle sort field change
  const setSortFieldHandler = (field: 'date' | 'total' | 'high') => {
    // If clicking the same field, toggle order
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // If clicking a different field, set it as the new sort field
      // and reset to descending order (showing highest/newest first)
      setSortField(field)
      setSortOrder('desc')
    }

    // Reset to first page when changing sort
    setPage(0)
  }

  // Filter scan groups by search term
  function handleSearchScans(e?: React.FormEvent) {
    e?.preventDefault()
    if (!searchTerm.trim()) {
      return
    }

    setSearchTerm(searchTerm.trim().toLowerCase())

    // Reset pagination
    setPage(0)

    fetchScanData()
  }

  function handleClearSearchScans() {
    setSearchTerm('')
    setSearchTermApplied(null)
    fetchScanData()
  }

  // Mapping of sort fields in UI to sort fields in API
  const sortFieldMapping = {
    date: 'scan_date',
    total: 'total_results',
    high: 'high_ranking_results',
  }

  // Open dialog with match details - shows all scan results for the selected batch
  const handleOpenMatchDetails = async (batchNumber: number) => {
    // Prevent duplicate calls if already loading

    setSelectedScanBatch(batchNumber) // Store batch number for the selected scan
    setDialogOpen(true) // Open the dialog immediately with loading indicator
  }

  // Close match details dialog
  const handleCloseDialog = () => {
    setDialogOpen(false)
  }

  // Format numbers with commas for thousands
  const formatNumber = (value: number | null | undefined) => {
    return Number.isFinite(value) ? Number(value).toLocaleString() : '-'
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'd MMM yyyy')
  }

  if (loading && scans.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
        <Button variant="contained" onClick={fetchScanData} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Admin Scan Dashboard
      </Typography>
      {(loading || summaryLoading) && (
        <Typography variant="caption" color="text.secondary">
          Updating {loading ? 'scans' : 'summary'}…
        </Typography>
      )}

      {/* Filter Controls */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
          }}
        >
          {/* organisation and Funding Need Filters */}
          <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, maxWidth: '70%' }}>
            <Autocomplete
              id="org-filter"
              sx={{ minWidth: 200 }}
              options={scanSummary.organisations || []}
              getOptionLabel={(option) => option.name}
              value={
                scanSummary.organisations?.find(
                  (org) => org.uuid === selectedOrg
                ) || null
              }
              onChange={(event, newValue) => {
                handleOrgChange(newValue?.uuid || '')
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organisation"
                  size="small"
                  placeholder="Search organisations"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.uuid}>
                  {option.name}
                </li>
              )}
              size="small"
              disableClearable={false}
              isOptionEqualToValue={(option, value) =>
                option.uuid === value.uuid
              }
            />

            <Autocomplete
              id="need-filter"
              sx={{ minWidth: 200 }}
              options={scanSummary.funding_needs || []}
              getOptionLabel={(option) => option.title}
              value={
                scanSummary.funding_needs?.find(
                  (need) => need.id === selectedNeed
                ) || null
              }
              onChange={(event, newValue) => {
                handleNeedChange(newValue?.id || '')
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Funding Need"
                  size="small"
                  placeholder="Search funding needs"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.title}
                </li>
              )}
              size="small"
              disableClearable={false}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Box>

          {/* Time Period Selector */}
          <ToggleButtonGroup
            color="primary"
            value={timeframe}
            exclusive
            onChange={handleTimeframeChange}
            aria-label="timeframe selector"
            size="small"
          >
            <ToggleButton value="7d">Week</ToggleButton>
            <ToggleButton value="30d">Month</ToggleButton>
            <ToggleButton value="90d">Quarter</ToggleButton>
            <ToggleButton value="all">All Time</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Funder and Opportunity Filters */}
        <Box sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
          <Autocomplete
            id="funder-filter"
            sx={{ minWidth: 300 }}
            options={scanSummary.funders || []}
            getOptionLabel={(option) => option.name}
            value={
              scanSummary.funders?.find(
                (funder) => funder.uuid === selectedFunder
              ) || null
            }
            onChange={(event, newValue) => {
              handleFunderChange(newValue?.uuid || '')
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Funder"
                size="small"
                placeholder="Filter by funder"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.uuid}>
                {option.name}
              </li>
            )}
            size="small"
            disableClearable={false}
            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
          />

          <Autocomplete
            id="opportunity-filter"
            sx={{ minWidth: 300 }}
            options={
              selectedFunder
                ? scanSummary.opportunities?.filter(
                    (opp) =>
                      scanSummary.funders?.find(
                        (f) => f.uuid === selectedFunder
                      )?.name === opp.funder_name
                  ) || []
                : scanSummary.opportunities || []
            }
            getOptionLabel={(option) =>
              `${option.fund_name} (${option.funder_name})`
            }
            value={
              scanSummary.opportunities?.find(
                (opp) => opp.uuid === selectedOpportunity
              ) || null
            }
            onChange={(event, newValue) => {
              handleOpportunityChange(newValue?.uuid || '')
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Funding Opportunity"
                size="small"
                placeholder="Filter by funding opportunity"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.uuid}>
                {option.fund_name} ({option.funder_name})
              </li>
            )}
            size="small"
            disableClearable={false}
            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
          />
        </Box>
      </Box>

      {/* Annotation coverage per candidate strategy. Above the scan metrics because
          it is the thing that tells you where to spend review effort next. */}
      {coverage && coverage.length > 0 ? (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Candidate strategy</TableCell>
                <TableCell align="right">Annotations</TableCell>
                <TableCell align="right">Scans</TableCell>
                <TableCell align="right">Matches</TableCell>
                <TableCell align="right">Annotators</TableCell>
                <TableCell>Last annotated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coverage.map((row) => (
                // Two targets on one line: the NAME opens the prompt, the rest of
                // the row goes to the A/B page. Anything but the name navigates,
                // so the default gesture is the common one.
                <TableRow
                  key={row.key}
                  hover
                  onClick={() => navigate('/scans/compare')}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      align="left"
                      onClick={(event) => {
                        // Without this the row handler fires too and the dialog
                        // opens on a page that is already navigating away.
                        event.stopPropagation()
                        openPrompt(row)
                      }}
                    >
                      {row.label}
                    </Link>
                  </TableCell>
                  <TableCell align="right">
                    {row.annotations === 0 ? (
                      <Tooltip title="Nothing judged yet on this strategy">
                        <Chip label="none" size="small" color="warning" />
                      </Tooltip>
                    ) : (
                      formatNumber(row.annotations)
                    )}
                  </TableCell>
                  <TableCell align="right">{formatNumber(row.scans)}</TableCell>
                  <TableCell align="right">
                    {formatNumber(row.matches)}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(row.annotators)}
                  </TableCell>
                  <TableCell>
                    {row.last_annotated_at
                      ? format(new Date(row.last_annotated_at), 'd MMM yyyy')
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      <Dialog
        open={promptFor !== null}
        onClose={() => setPromptFor(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {promptFor?.label}
          <Typography variant="body2" color="text.secondary">
            {promptFor?.prompt} - the literal template this strategy runs
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {promptText === null ? (
            <CircularProgress size={24} />
          ) : (
            // Monospace and pre-wrap: this is a prompt, and its line breaks and
            // indentation are part of what is being reviewed.
            <Typography
              component="pre"
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                m: 0,
              }}
            >
              {promptText}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Key Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 1 }}>
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary">
                Total Scans
              </Typography>
              <Typography variant="h4">
                {formatNumber(scanSummary.total_scans)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 1 }}>
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary">
                Organisations in Results
              </Typography>
              <Typography variant="h4">
                {formatNumber(scanSummary.total_organisations)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 1 }}>
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary">
                Total Potential Matches
              </Typography>
              <Typography variant="h4">
                {formatNumber(scanSummary.total_potential_matches)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 1 }}>
            <CardContent>
              <Typography variant="subtitle1" color="text.secondary">
                High Ranking Matches
              </Typography>
              <Typography variant="h4">
                {formatNumber(scanSummary.high_ranking_matches)} (
                {(scanSummary.high_ranking_percentage ?? 0).toFixed(1)}%)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Scan Volume Chart */}
      <Card sx={{ mb: 3, p: 1 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Scan Volume
          </Typography>
          <Box sx={{ height: 500 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={scanSummary.scan_count_by_period || []}
                margin={{ top: 5, right: 30, left: 20, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="period"
                  tickFormatter={(value: string) => {
                    // Format dates differently based on timeframe
                    if (timeframe === '7d') {
                      // For daily view, show day of week and date
                      const date = new Date(value)
                      // Check if date is valid
                      return isNaN(date.getTime())
                        ? value
                        : format(date, 'EEE, MMM d') // e.g., "Mon, Jan 15"
                    } else {
                      // For other timeframes, show week starting date
                      return `Week of ${value}`
                    }
                  }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  // Ensure all ticks are displayed (don't skip any days)
                  interval={0}
                />
                <YAxis tickFormatter={(value: number) => value.toString()} />
                <RechartsTooltip
                  formatter={(value: number, name: string) => {
                    return [`${value.toLocaleString()} scans`, 'Count']
                  }}
                  labelFormatter={(label: string) => {
                    if (timeframe === '7d') {
                      // Show full date for daily view
                      const date = new Date(label)
                      // Check if date is valid
                      return isNaN(date.getTime())
                        ? `Date: ${label}`
                        : format(date, 'EEEE, MMMM d, yyyy') // e.g., "Monday, January 15, 2024"
                    } else {
                      return `Week Starting: ${label}`
                    }
                  }}
                />
                <Bar dataKey="count" fill={COLOURS[2]} name="Scans" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Top Organisations and Top Funders */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', p: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  Top Organisations by Scan Volume
                </Typography>
                <Tooltip title="Click on a bar to filter by that organisation">
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: 'primary.main',
                      fontSize: '0.75rem',
                      cursor: 'help',
                    }}
                  >
                    <span role="img" aria-label="Clickable">
                      👆
                    </span>{' '}
                    Clickable
                  </Box>
                </Tooltip>
              </Box>
              <Box sx={{ height: 500 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={scanSummary.top_organisations || []}
                    layout="vertical"
                    margin={{ top: 5, right: 40, left: -10, bottom: 5 }}
                    barSize={20}
                    maxBarSize={30}
                    onClick={(data: any) => {
                      if (
                        data &&
                        data.activePayload &&
                        data.activePayload.length > 0
                      ) {
                        const clickedOrg = data.activePayload[0].payload

                        if (!scanSummary.organisations) {
                          return
                        }

                        // Find the organisation in the full list by name
                        const orgMatch = scanSummary.organisations.find(
                          (org) => org.name === clickedOrg.name
                        )
                        if (orgMatch) {
                          // Set the organisation filter
                          handleOrgChange(orgMatch.uuid)
                        }
                      }
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={true}
                      vertical={false}
                    />
                    <XAxis
                      type="number"
                      tickFormatter={(value: number) => {
                        return value.toLocaleString()
                      }}
                      domain={[0, 'dataMax * 1.3']}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{
                        fontSize: 12,
                        textAnchor: 'start',
                        dx: -125,
                        cursor: 'pointer',
                      }}
                      tickMargin={0}
                      width={140}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip
                      formatter={(value: number) => [`${value} scans`, 'Count']}
                      content={({ active, payload }: any) => {
                        if (active && payload && payload.length) {
                          return (
                            <div
                              style={{
                                backgroundColor: '#fff',
                                border: '1px solid #ccc',
                                padding: '10px',
                                borderRadius: '4px',
                                fontSize: '12px',
                              }}
                            >
                              <p style={{ margin: 0 }}>
                                <b>{payload[0].payload.name}</b>
                              </p>
                              <p style={{ margin: 0 }}>
                                {payload[0].value.toLocaleString()} scans
                              </p>
                              <p
                                style={{
                                  margin: '5px 0 0 0',
                                  fontSize: '10px',
                                  color: '#666',
                                  fontStyle: 'italic',
                                }}
                              >
                                Click to filter by this organisation
                              </p>
                            </div>
                          )
                        }
                        return null
                      }}
                    />
                    <Bar
                      dataKey="scans"
                      radius={[0, 4, 4, 0]}
                      // Customize fill color - highlight if this organisation is selected in filters
                    >
                      {(scanSummary.top_organisations || []).map(
                        (entry, index) => {
                          if (!scanSummary.organisations) {
                            return
                          }

                          const orgMatch = scanSummary.organisations.find(
                            (org) =>
                              org.name === entry.name &&
                              org.uuid === selectedOrg
                          )
                          return (
                            <Cell
                              key={`cell-${index}`}
                              fill={orgMatch ? '#1976d2' : COLOURS[0]}
                            />
                          )
                        }
                      )}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', p: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  Top Funders by Number of High Ranking Matches
                </Typography>
                <Tooltip title="Click on a bar to filter by that funder">
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: 'primary.main',
                      fontSize: '0.75rem',
                      cursor: 'help',
                    }}
                  >
                    <span role="img" aria-label="Clickable">
                      👆
                    </span>{' '}
                    Clickable
                  </Box>
                </Tooltip>
              </Box>
              <Box sx={{ height: 500 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={scanSummary.top_funders || []}
                    layout="vertical"
                    margin={{ top: 5, right: 40, left: -10, bottom: 5 }}
                    barSize={20}
                    maxBarSize={30}
                    onClick={(data: any) => {
                      if (
                        data &&
                        data.activePayload &&
                        data.activePayload.length > 0
                      ) {
                        const clickedFunder = data.activePayload[0].payload

                        // Find the funder in the full list by name
                        const funderMatch = scanSummary.funders.find(
                          (funder) => funder.name === clickedFunder.name
                        )
                        if (funderMatch) {
                          // Set the funder filter
                          handleFunderChange(funderMatch.uuid)
                        }
                      }
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={true}
                      vertical={false}
                    />
                    <XAxis
                      type="number"
                      tickFormatter={(value: number) => {
                        return value.toLocaleString()
                      }}
                      domain={[0, 'dataMax * 1.3']}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{
                        fontSize: 12,
                        textAnchor: 'start',
                        dx: -125,
                        cursor: 'pointer',
                      }}
                      tickMargin={0}
                      width={140}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip
                      formatter={(value: number) => [
                        `${value} high ranking matches`,
                        'Count',
                      ]}
                      content={({ active, payload }: any) => {
                        if (active && payload && payload.length) {
                          return (
                            <div
                              style={{
                                backgroundColor: '#fff',
                                border: '1px solid #ccc',
                                padding: '10px',
                                borderRadius: '4px',
                                fontSize: '12px',
                              }}
                            >
                              <p style={{ margin: 0 }}>
                                <b>{payload[0].payload.name}</b>
                              </p>
                              <p style={{ margin: 0 }}>
                                {payload[0].value.toLocaleString()} high ranking
                                matches
                              </p>
                              <p
                                style={{
                                  margin: '5px 0 0 0',
                                  fontSize: '10px',
                                  color: '#666',
                                  fontStyle: 'italic',
                                }}
                              >
                                Click to filter by this funder
                              </p>
                            </div>
                          )
                        }
                        return null
                      }}
                    />
                    <Bar dataKey="high_ranking_matches" radius={[0, 4, 4, 0]}>
                      {(scanSummary.top_funders || []).map((entry, index) => {
                        const funderMatch = scanSummary.funders.find(
                          (funder) =>
                            funder.name === entry.name &&
                            funder.uuid === selectedFunder
                        )
                        return (
                          <Cell
                            key={`cell-${index}`}
                            fill={funderMatch ? '#1976d2' : COLOURS[4]}
                          />
                        )
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Match Results Table - Grouped by Funding Need */}
      <Card sx={{ p: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6">Scans</Typography>
              {searchTermApplied && searchTermApplied !== '' && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Showing results for "{searchTermApplied}"
                  </Typography>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={handleClearSearchScans}
                    sx={{ ml: 1 }}
                  >
                    Clear Search
                  </Button>
                </Box>
              )}
            </Box>
            {/* Search field */}
            <form onSubmit={handleSearchScans}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  maxWidth: 450,
                  width: '100%',
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by organisation or funding need..."
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{
                    ml: 1,
                    height: 40,
                    width: 120,
                  }}
                  onClick={handleSearchScans}
                  disabled={searchTerm.trim() === ''}
                >
                  Search
                </Button>
              </Box>
            </form>
          </Box>

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="match results table">
              <TableHead>
                <TableRow>
                  <TableCell>Funding Need</TableCell>
                  <TableCell>Organisation</TableCell>
                  <TableCell
                    align="center"
                    onClick={() => setSortFieldHandler('date')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Scan Date{' '}
                    {sortField === 'date' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => setSortFieldHandler('total')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Total Results{' '}
                    {sortField === 'total' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => setSortFieldHandler('high')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    High Ranking Results{' '}
                    {sortField === 'high' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No scan data available for the selected filters
                    </TableCell>
                  </TableRow>
                ) : (
                  // Show scans directly - pagination is handled server-side
                  scans.map((scan: Scan) => {
                    return (
                      <TableRow key={scan.batch_number}>
                        <TableCell>
                          <Typography variant="body1">
                            {scan.funding_need_title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {scan.org_name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {formatDate(scan.scan_date)}
                        </TableCell>
                        <TableCell align="center">
                          {scan.total_results}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${scan.high_ranking_results} (${
                              scan.total_results > 0
                                ? Math.round(
                                    (scan.high_ranking_results /
                                      scan.total_results) *
                                      100
                                  )
                                : 0
                            }%)`}
                            style={{
                              backgroundColor: '#f5f5f5',
                              color: 'rgba(0, 0, 0, 0.87)',
                              minWidth: '80px',
                            }}
                            size="small"
                          />
                        </TableCell>
                        {/* One row, never wrapped. A third icon was enough to
                            push these onto three lines and stretch every row. */}
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            flexWrap="nowrap"
                          >
                            <Tooltip title="View All Batch Matches">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenMatchDetails(scan.batch_number)
                                }
                                color="primary"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={`${annotationLabel(
                                annotationStatus.get(
                                  `${scan.need_id}:${scan.batch_number}`
                                )
                              )}. ${
                                requested.has(
                                  `${scan.need_id}:${scan.batch_number}`
                                )
                                  ? 'Requested - click to withdraw'
                                  : 'Click to request annotation'
                              }`}
                            >
                              <IconButton
                                size="small"
                                // Colour carries PROGRESS, the outline carries the
                                // REQUEST. They are independent: a scan can be
                                // fully annotated and still queued, or untouched
                                // and unrequested.
                                sx={{
                                  color: annotationColour(
                                    annotationStatus.get(
                                      `${scan.need_id}:${scan.batch_number}`
                                    )
                                  ),
                                  border: requested.has(
                                    `${scan.need_id}:${scan.batch_number}`
                                  )
                                    ? '2px solid currentColor'
                                    : '2px solid transparent',
                                }}
                                onClick={() =>
                                  toggleAnnotationRequest(
                                    scan.need_id,
                                    scan.batch_number,
                                    requested.has(
                                      `${scan.need_id}:${scan.batch_number}`
                                    )
                                  )
                                }
                              >
                                <RequestAnnotationIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Compare engines (shadow runs)">
                              <IconButton
                                size="small"
                                component={RouterLink}
                                to={`/scans/compare?need_id=${scan.need_id}&batch_number=${scan.batch_number}`}
                                color="primary"
                              >
                                <CompareIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalItems ?? -1} // Total from the API; -1 means unknown
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {dialogOpen && scans.length > 0 && (
        <ScanDashboardMatchDetails
          dialogOpen={dialogOpen}
          handleCloseDialog={handleCloseDialog}
          selectedScanBatch={selectedScanBatch}
          scanDate={formatDate(
            scans.find((s) => s.batch_number === selectedScanBatch)
              ?.scan_date || ''
          )}
          fundingNeedTitle={
            scans.find((s) => s.batch_number === selectedScanBatch)
              ?.funding_need_title || ''
          }
          orgName={
            scans.find((s) => s.batch_number === selectedScanBatch)?.org_name ||
            ''
          }
          fundingNeedId={
            scans.find((s) => s.batch_number === selectedScanBatch)?.need_id ||
            null
          }
        />
      )}
    </Box>
  )
}

export default ScanDashboard
